import { call, put, takeLatest, select, takeLeading } from 'redux-saga/effects';
import logger from '@tcp/core/src/utils/loggerInstance';
import CHECKOUT_ACTIONS from '@tcp/core/src/components/features/CnC/Checkout/container/Checkout.action';
import BAG_PAGE_ACTIONS from '@tcp/core/src/components/features/CnC/BagPage/container/BagPage.actions';
import BAG_SELECTORS from '@tcp/core/src/components/features/CnC/BagPage/container/BagPage.selectors';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
} from '@tcp/core/src/utils/labels/labelUtilty';
import {
  confirmStartCheckout,
  getUnqualifiedItemsCall as getUnqualifiedItemsCallSaga,
} from '@tcp/core/src/components/features/CnC/BagPage/container/BagPage.saga';
import { getCartDetailsCompleteCallforWindows } from '@tcp/core/src/reduxStore/selectors/session.selectors.js';
import { isSafariBrowser } from '@tcp/core/src/utils';
import ADYENPAYMENT_CONSTANTS from './AdyenPayment.constants';
import makeSessionsSetupCall, {
  makePaymentsCall,
} from '../../../../../services/abstractors/CnC/Adyen';
import { validateReduxCache } from '../../../../../utils/cache.util';
import { setAdyenSessionData, setAdyenSetupData } from './AdyenPayment.actions';
import { AddToCartError } from '../../../../features/CnC/AddedToBag/container/AddedToBag.actions';
import { setApplePayCartLoadingState } from '../../../../features/CnC/CartItemTile/container/CartItemTile.actions';
import {
  ErrorLabels,
  GLOBAL_ERRORMESSAGES_LABEL,
  lblErrorMessagesDefault,
} from '../../../../features/CnC/BagPage/container/Error.labels';

export function* startAdyenApplePayInventory({ payload }) {
  const { isBagPage } = payload;
  try {
    if (isBagPage) {
      yield call(getUnqualifiedItemsCallSaga, true);
    }
  } catch (error) {
    const errorMapping = yield select(BAG_SELECTORS.getErrorMapping);
    const errCode = error.errorCodes;
    const errMsg = errorMapping[errCode] || errorMapping.PAYPAL_CC_ERROR_CODE_GENERIC_ERROR;
    yield put(AddToCartError(errMsg));

    yield put(
      CHECKOUT_ACTIONS.setServerErrorCheckout({
        errorMessage: errMsg,
        component: 'PAGE',
        isSubmitOrderError: true,
      })
    );
    logger.error({
      error,
      extraData: {
        isBagPage,
        component: 'AdyenPaymentSaga - startAdyenApplePayInventory',
      },
    });
    throw new Error('Failed to start Adyen payment');
  }
}

export function* startAdyenPayments({ payload }) {
  const { data, isBagPage, resolve, reject } = payload;
  let oOSModalOpen = false;
  try {
    if (isBagPage) {
      yield call(getUnqualifiedItemsCallSaga);
      oOSModalOpen = yield call(confirmStartCheckout);
    }
    if (!oOSModalOpen || !isBagPage) {
      try {
        const result = yield call(makePaymentsCall, data, isBagPage);
        if (!result.resultCode) {
          reject(new Error('Payment failed'));
          return;
        }
        resolve(result);
      } catch (e) {
        const errorMapping = yield select(BAG_SELECTORS.getErrorMapping);
        const errCode = e.errorCode;
        const errMsg = errorMapping[errCode] || errorMapping.PAYPAL_CC_ERROR_CODE_GENERIC_ERROR;
        yield put(AddToCartError(errMsg));
        yield put(
          CHECKOUT_ACTIONS.setServerErrorCheckout({
            errorMessage: errMsg,
            component: 'PAGE',
            isSubmitOrderError: true,
          })
        );
        reject(e);
      }
    } else {
      yield put(BAG_PAGE_ACTIONS.setPayPalModalActionOOS(true));
      reject(new Error('Out of stock'));
    }
  } catch (error) {
    const errorMapping = yield select(BAG_SELECTORS.getErrorMapping);
    const errCode = error.errorCodes;
    const errMsg = errorMapping[errCode] || errorMapping.PAYPAL_CC_ERROR_CODE_GENERIC_ERROR;
    yield put(AddToCartError(errMsg));

    yield put(
      CHECKOUT_ACTIONS.setServerErrorCheckout({
        errorMessage: errMsg,
        component: 'PAGE',
        isSubmitOrderError: true,
      })
    );
    reject(new Error('Failed to start Adyen payment'));
  }
}

export function* createSession({ payload }) {
  const { isBagPage = false, cacheTTL = 0 } = payload || {};
  try {
    yield put(setAdyenSetupData(null));
    yield put(AddToCartError(null));
    yield put(CHECKOUT_ACTIONS.setServerErrorCheckout({}));
    yield put(setApplePayCartLoadingState(true));
    const isAddToBagCallMade = yield select(BAG_SELECTORS.getAddToBagCallMade);
    const isCartDetailCompleteCallEnabled = yield select(getCartDetailsCompleteCallforWindows);
    if (isAddToBagCallMade && isBagPage && (isSafariBrowser() || isCartDetailCompleteCallEnabled)) {
      yield call(getUnqualifiedItemsCallSaga, true);
      yield put(BAG_PAGE_ACTIONS.setAddToBagCallMade(false));
    }
    const result = yield call(makeSessionsSetupCall, isBagPage);
    if (!result || !result.sessionData) {
      const errMsg = getDynamicLabelValues(
        'dynamic.error_messages.lbl_errorMessages_ERR_ADYEN_GENERIC_SESSIONS'
      );
      yield put(
        CHECKOUT_ACTIONS.setServerErrorCheckout({
          errorMessage: errMsg,
          component: 'PAGE',
        })
      );
      yield put(AddToCartError(errMsg));
      throw new Error('Session creation failed: No session data returned');
    }

    yield put(setAdyenSessionData({ ...result, cacheTTL }));
  } catch (error) {
    const adyenPaypalrrMsg = getDynamicLabelValues(
      'dynamic.error_messages.lbl_errorMessages_ERR_ADYEN_GENERIC_SESSIONS'
    );
    const defaultErrorMsg = getLabelFromCopyOrFromTranslation(
      `${GLOBAL_ERRORMESSAGES_LABEL}.${lblErrorMessagesDefault}`,
      ErrorLabels
    );
    const errorMapping = yield select(BAG_SELECTORS.getErrorMapping);
    const errCode = error && error.errorCode;
    const isBasketError = errCode === 'CHECKOUT_BASKET_ERROR';
    const errMsg = isBasketError ? defaultErrorMsg : errorMapping[errCode] || adyenPaypalrrMsg;
    yield put(
      CHECKOUT_ACTIONS.setServerErrorCheckout({
        errorMessage: errMsg,
        component: 'PAGE',
      })
    );
    yield put(AddToCartError(errMsg));
    console.error('Error during session creation:', error);
  }
}

export function* AdyenPaymentSaga() {
  const CachedCreateSession = validateReduxCache(createSession);
  yield takeLatest(ADYENPAYMENT_CONSTANTS.START_ADYEN_PAYMENT, startAdyenPayments);
  yield takeLatest(ADYENPAYMENT_CONSTANTS.CREATE_APPLEPAY_INVENTORY, startAdyenApplePayInventory);
  yield takeLeading(ADYENPAYMENT_CONSTANTS.CREATE_ADYEN_SESSION, CachedCreateSession);
}

export default AdyenPaymentSaga;
